import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  DollarSign, 
  Receipt, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  FileText, 
  Download, 
  ArrowUpRight,
  ShieldCheck,
  CreditCard
} from 'lucide-react';
import { ProjectRevenueRecord, InvoiceStatus } from '@ftt/shared';

export const ClientInvoicesPage: React.FC = () => {
  const { data: invoicesRes, isLoading } = useQuery({
    queryKey: ['client-invoices'],
    queryFn: () => api.get('/client/invoices').then((res) => res.data.data),
  });

  const invoices: ProjectRevenueRecord[] = invoicesRes || [];

  // Summary Metrics
  const totalContractValue = invoices.reduce((sum, inv) => sum + inv.contractValue, 0);
  const totalPaid = invoices.reduce((sum, inv) => sum + inv.receivedAmount, 0);
  const totalPending = invoices.reduce((sum, inv) => sum + inv.pendingAmount, 0);

  const getStatusBadge = (status: InvoiceStatus) => {
    switch (status) {
      case 'PAID':
        return { label: 'Paid & Settled', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'PARTIALLY_PAID':
        return { label: 'Partially Paid', color: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'ISSUED':
        return { label: 'Issued / Pending', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'OVERDUE':
        return { label: 'Overdue', color: 'bg-rose-50 text-rose-700 border-rose-200' };
      case 'DRAFT':
      default:
        return { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200' };
    }
  };

  return (
    <div className="space-y-8 font-sans">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-600 font-mono uppercase tracking-widest font-bold">
              FINANCIALS &amp; CONTRACTS
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-500 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900 flex items-center gap-2.5">
            <Receipt className="w-7 h-7 text-brand-600" />
            <span>Project Invoices &amp; Accounts</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600">
            Review service agreements, issued invoices, payment schedules, and verified receipts.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-6 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Total Contract Value</span>
            <DollarSign className="w-4 h-4 text-brand-600" />
          </div>
          <div className="text-2xl font-bold font-serif text-slate-900">
            ₹{totalContractValue.toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-slate-500 font-mono">Agreed Production Contracts</div>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Paid &amp; Realized</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold font-serif text-emerald-700">
            ₹{totalPaid.toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-emerald-600 font-mono">Verified Receipts</div>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Outstanding Balance</span>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-2xl font-bold font-serif text-amber-700">
            ₹{totalPending.toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-amber-600 font-mono">Pending Milestone Invoices</div>
        </div>
      </div>

      {/* Invoices Table & Cards */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-500">Loading invoice records...</div>
      ) : invoices.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-editorial-border text-center space-y-4">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-800">No Invoices Found</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            You do not currently have any invoices or payment schedules generated under your account.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-editorial-border shadow-xs overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-serif font-bold text-lg text-slate-900">
              Billing History &amp; Invoice Schedule
            </h3>
            <span className="text-xs font-mono text-slate-500">
              Official Tax Invoices
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-6">Invoice #</th>
                  <th className="py-3 px-6">Project &amp; Service</th>
                  <th className="py-3 px-6 text-right">Contract Value</th>
                  <th className="py-3 px-6 text-right">Paid Amount</th>
                  <th className="py-3 px-6 text-right">Balance Due</th>
                  <th className="py-3 px-6">Status</th>
                  <th className="py-3 px-6">Due Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-mono">
                {invoices.map((inv) => {
                  const badge = getStatusBadge(inv.invoiceStatus);

                  return (
                    <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-6 font-bold text-brand-700">
                        {inv.invoiceNumber}
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-serif font-bold text-sm text-slate-900 font-sans">
                          {inv.projectTitle}
                        </div>
                        <div className="text-[11px] text-slate-500 font-sans">
                          {inv.serviceType.replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right font-bold text-slate-900">
                        ₹{inv.contractValue.toLocaleString('en-IN')}
                      </td>
                      <td className="py-4 px-6 text-right text-emerald-700 font-semibold">
                        ₹{inv.receivedAmount.toLocaleString('en-IN')}
                      </td>
                      <td className="py-4 px-6 text-right text-amber-700 font-bold">
                        ₹{inv.pendingAmount.toLocaleString('en-IN')}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold border ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-slate-600">
                        {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 font-sans">
            <span>Invoices issued by <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong>.</span>
            <div className="flex items-center gap-2 text-brand-700 font-semibold">
              <ShieldCheck className="w-4 h-4 text-brand-600" />
              <span>Official Tax &amp; GST Compliant Records</span>
            </div>
          </div>

        </div>
      )}

    </div>
  );
};
